import React from 'react';
import { AlertTriangle, Mail, X } from 'lucide-react';
import { useLanguage } from '../App';

/**
 * BannedUserBanner - Shows a prominent red banner for banned users
 * Displays on every dashboard page to inform the user of their account status
 */
const BannedUserBanner = ({ user, onDismiss }) => {
  const { language } = useLanguage();
  
  if (!user?.is_banned) return null;
  
  const banReason = user.ban_reason || (language === 'nl' 
    ? 'Geen reden opgegeven' 
    : language === 'es' 
    ? 'No se proporcionó razón' 
    : 'No reason provided');
  
  const translations = {
    en: {
      title: 'Account Suspended',
      message: 'Your account has been temporarily suspended. While suspended, you cannot create listings, send messages, or make offers.',
      reason: 'Reason',
      contact: 'Contact Support',
      contactMessage: 'If you believe this is a mistake, please contact our support team.',
      email: 'support@yourbuysite.com'
    },
    nl: {
      title: 'Account Geblokkeerd',
      message: 'Je account is tijdelijk geblokkeerd. Tijdens de blokkering kun je geen advertenties plaatsen, berichten versturen of biedingen doen.',
      reason: 'Reden',
      contact: 'Contact Opnemen',
      contactMessage: 'Als je denkt dat dit een vergissing is, neem dan contact op met ons supportteam.',
      email: 'support@yourbuysite.com'
    },
    es: {
      title: 'Cuenta Suspendida',
      message: 'Tu cuenta ha sido suspendida temporalmente. Mientras estés suspendido, no puedes crear anuncios, enviar mensajes ni hacer ofertas.',
      reason: 'Razón',
      contact: 'Contactar Soporte',
      contactMessage: 'Si crees que esto es un error, por favor contacta a nuestro equipo de soporte.',
      email: 'support@yourbuysite.com'
    }
  };
  
  const t = translations[language] || translations.en;
  
  return (
    <div className="bg-red-500/10 border-l-4 border-red-500 rounded-r-xl p-4 mb-6" data-testid="banned-user-banner">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-6 h-6 text-red-500" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-lg font-bold text-red-600 dark:text-red-400">
              {t.title}
            </h3>
            {onDismiss && (
              <button 
                onClick={onDismiss}
                className="p-1 hover:bg-red-500/10 rounded-lg transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5 text-red-500/70" />
              </button>
            )}
          </div>
          
          <p className="text-sm text-red-600/80 dark:text-red-400/80 mt-1">
            {t.message}
          </p>
          
          {/* Ban Reason */}
          <div className="mt-3 p-3 bg-red-500/10 rounded-lg">
            <p className="text-xs font-semibold text-red-600/70 dark:text-red-400/70 uppercase tracking-wide mb-1">
              {t.reason}
            </p>
            <p className="text-sm text-red-700 dark:text-red-300 font-medium">
              {banReason}
            </p>
          </div>
          
          {/* Contact Support */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-xs text-muted-foreground">
              {t.contactMessage}
            </p>
            <a
              href={`mailto:${t.email}?subject=Account%20Suspension%20Appeal`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
              data-testid="contact-support-btn"
            >
              <Mail className="w-4 h-4" />
              {t.contact}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannedUserBanner;
